import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText'; 
import { PREFIX } from '../helpers/config.js';

gsap.registerPlugin(ScrollTrigger, SplitText);

const SELECTOR = `data-${PREFIX}-hero`;

export class MyHR {
  constructor(element) {
    if (!(element instanceof HTMLElement)) return;
    this._element = element;
    
    this._main         = this._element.querySelector(`[data-${PREFIX}-hero-main]`);
    this._scroller     = this._element.querySelector(`[data-${PREFIX}-hero-scroller]`);
    this._slides       = Array.from(this._element.querySelectorAll(`[data-${PREFIX}-hero-slide]`));
    this._bullets      = Array.from(this._element.querySelectorAll(`[data-${PREFIX}-hero-bullet]`));
    this._progressBar  = this._element.querySelector(`[data-${PREFIX}-hero-progress]`);
    this._indicator    = this._element.querySelector(`[data-${PREFIX}-hero-indicator]`);

    if (!this._main || !this._scroller) return;
    this._init();
  }

  _init() {
    gsap.set(this._scroller, { autoAlpha: 0 });
    
    this._splits = this._slides.map(slide => {
      const target = slide.querySelectorAll(`[data-${PREFIX}-split]`);
      return Array.from(target).map(el => new SplitText(el, {
        type: 'words',
        wordsClass: 'sp-word'
      }));
    });

    if (this._slides.length > 0) {
      gsap.set(this._slides, { autoAlpha: 0 });
      gsap.set(this._slides[0], { autoAlpha: 1 });
    }

    this._createTimeline();
  }

  _createTimeline() {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: this._element,
        start: "top top",
        end: "+=800%", 
        pin: true,
        scrub: 0.5, // Reducimos ligeramente el scrub para mayor precisión de respuesta
        anticipatePin: 1,
        // Usamos onUpdate para una sincronización total de los bullets
        onUpdate: (self) => this._syncBulletsWithScroll(self.progress)
      }
    });

    const masterTime = 30; 
    const transitionTime = 3; 
    const textRevealTime = 5; 
    const segmentTime = masterTime / this._slides.length; 

    // 1. REVEAL INICIAL (Main -> Scroller)
    tl.to(this._main, { yPercent: -100, autoAlpha: 0, duration: 4 });
    tl.fromTo(this._scroller, 
      { yPercent: 100, autoAlpha: 0 },
      { yPercent: 0, autoAlpha: 1, duration: 4 }, "<"
    );

    const startSlidesLabel = "start-slides";
    tl.addLabel(startSlidesLabel);

    // 2. BARRA DE PROGRESO (0% a 100% lineal)
    if (this._progressBar && this._indicator) {
      tl.to(this._progressBar, { width: "100%", ease: "none", duration: masterTime }, startSlidesLabel);
      tl.to(this._indicator, { left: "100%", ease: "none", duration: masterTime }, startSlidesLabel);
    }

    // 3. COREOGRAFÍA DE DIAPOSITIVAS
    this._slides.forEach((slide, i) => {
      const activationPoint = i * segmentTime;
      const label = `bullet-${i}`;

      tl.addLabel(label, `${startSlidesLabel}+=${activationPoint}`);

      if (i > 0) {
        // La transición visual ocurre JUSTO antes del punto de activación
        tl.to(this._slides[i-1], { autoAlpha: 0, yPercent: -10, duration: transitionTime }, `${label}-=${transitionTime / 2}`)
          .fromTo(slide, 
            { autoAlpha: 0, yPercent: 15 }, 
            { autoAlpha: 1, yPercent: 0, duration: transitionTime }, 
            `${label}-=${transitionTime / 2}`
          );
      }

      // SplitText (Revelado de palabras)
      const slideWords = [];
      this._splits[i].forEach(s => slideWords.push(...s.words));
      if (slideWords.length > 0) {
        tl.to(slideWords, {
          stagger: {
            amount: textRevealTime,
            onStart: function() { this.targets()[0].classList.add('active'); },
            onReverseComplete: function() { this.targets()[0].classList.remove('active'); }
          }
        }, label);
      }
    });

    tl.to({}, { duration: 2 }); 
  }

  /**
   * Nueva función de sincronización basada en el progreso total (0 a 1)
   * Esto garantiza que el bullet cambie exactamente cuando debe, subiendo o bajando.
   */
  _syncBulletsWithScroll(progress) {
    // Calculamos en qué punto del progreso total (0 a 1) empiezan las slides
    // El reveal inicial ocupa aprox el 20% del timeline total (800% end vs 4 duration)
    const slidesStart = 0.15; // Ajuste empírico según tu timeline
    const slidesEnd = 0.95;
    
    if (progress < slidesStart) {
        this._updateBullets(0);
        return;
    }

    // Mapeamos el progreso actual al índice de la slide
    const totalSlides = this._slides.length;
    const currentProgress = (progress - slidesStart) / (slidesEnd - slidesStart);
    
    // Calculamos el índice basándonos en umbrales (0.33, 0.66, etc.)
    let activeIndex = Math.floor(currentProgress * totalSlides);
    
    // Aseguramos que no se pase del índice máximo
    activeIndex = Math.max(0, Math.min(activeIndex, totalSlides - 1));
    
    this._updateBullets(activeIndex);
  }

  _updateBullets(index) {
    // 1. Actualizar Bullets superiores (Design | Manufacture | Build)
    this._bullets.forEach((b, i) => {
      b.classList.toggle('is-active', i === index);
    });

    // 2. Actualizar el Menú de Títulos Lateral (Solo 1 vez en el DOM)
    const sideTitles = this._element.querySelectorAll('.hero_slide-title-item');
    sideTitles.forEach((item, i) => {
      item.classList.toggle('is-active', i === index);
    });
  }

  static initAll() {
    const nodes = document.querySelectorAll(`[${SELECTOR}]`);
    nodes.forEach(el => { 
      if (!el._myhrInstance) el._myhrInstance = new MyHR(el); 
    });
  }
}

export default MyHR;